import { useForm, useFieldArray } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Loader2, Plus, Trash2 } from "lucide-react";

const customFieldSchema = z.object({
  key: z.string().min(1, "Key is required"),
  label: z.string().min(1, "Label is required"),
  type: z.enum(["text", "select", "boolean"]).default("text"),
  options: z.string().optional(),
  required: z.boolean().default(false),
});

const productFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  defaultPrice: z.string().min(1, "Price is required"),
  currency: z.string().default("GBP"),
  defaultRecurrence: z.enum(["monthly", "annual"]).default("monthly"),
  skuCode: z.string().optional(),
  defaultServiceTypeId: z.string().optional(),
  customFields: z.array(customFieldSchema).optional(),
});

type ProductFormValues = z.infer<typeof productFormSchema>;

interface ProductFormProps {
  defaultValues?: Partial<ProductFormValues>;
  onSubmit: (data: ProductFormValues) => void;
  isPending?: boolean;
}

export function ProductForm({
  defaultValues,
  onSubmit,
  isPending,
}: ProductFormProps) {
  const { data: serviceTypes } = useQuery<
    Array<{ id: string; label: string; key: string }>
  >({
    queryKey: ["/api/service-types"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      description: "",
      defaultPrice: "",
      currency: "GBP",
      defaultRecurrence: "monthly",
      skuCode: "",
      defaultServiceTypeId: "",
      customFields: [],
      ...defaultValues,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "customFields",
  });

  function handleSubmit(data: ProductFormValues) {
    // Transform customFields into customFieldsConfig for the API
    const { customFields, ...rest } = data;
    const payload: any = { ...rest };
    payload.defaultServiceTypeId = payload.defaultServiceTypeId || null;
    if (customFields && customFields.length > 0) {
      payload.customFieldsConfig = {
        fields: customFields.map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          required: f.required,
          ...(f.type === "select" && f.options
            ? { options: f.options.split(",").map((o) => o.trim()).filter(Boolean) }
            : {}),
        })),
      };
    } else {
      payload.customFieldsConfig = { fields: [] };
    }
    onSubmit(payload);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Name *</FormLabel>
              <FormControl>
                <Input placeholder="Web Hosting Pro" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Product description..."
                  className="min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="defaultPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Default Price (ex. VAT) *</FormLabel>
                <FormControl>
                  <Input placeholder="99.00" type="number" step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultRecurrence"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Recurrence</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="skuCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SKU Code</FormLabel>
              <FormControl>
                <Input placeholder="WHP-001" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="defaultServiceTypeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Default Service Type</FormLabel>
              <Select
                value={field.value || "none"}
                onValueChange={(value) =>
                  field.onChange(value === "none" ? "" : value)
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Optional default mapping" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">No default service type</SelectItem>
                  {(serviceTypes ?? []).map((serviceType) => (
                    <SelectItem key={serviceType.id} value={serviceType.id}>
                      {serviceType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Prefills the service type when this product is sold as a service.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Custom Fields Config */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Custom Service Fields</p>
              <p className="text-xs text-muted-foreground">
                Define fields that agents fill in when adding this product as a service (e.g. hosting provider, CMS type).
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ key: "", label: "", type: "text", options: "", required: false })}
            >
              <Plus className="mr-1 h-3 w-3" />
              Add Field
            </Button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Field {index + 1}</p>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => remove(index)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name={`customFields.${index}.key`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="field_key" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`customFields.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Display Label" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`customFields.${index}.type`}
                  render={({ field }) => (
                    <FormItem>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="select">Dropdown</SelectItem>
                          <SelectItem value="boolean">Yes/No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
              {form.watch(`customFields.${index}.type`) === "select" && (
                <FormField
                  control={form.control}
                  name={`customFields.${index}.options`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Option1, Option2, Option3" {...field} />
                      </FormControl>
                      <FormDescription className="text-xs">Comma-separated options</FormDescription>
                    </FormItem>
                  )}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {defaultValues?.name ? "Update Product" : "Create Product"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
